import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, of } from 'rxjs';
import { take } from 'rxjs/operators';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { ServiceProcessService } from '../../_services/service-process/service-process.service';
import { ServiceProcessFlag } from '../../_models/admin/service-process.models';
import { AppSettingsEditorComponent } from '../app-settings/app-settings-editor.component';
import { NudgerRulesEditorComponent } from '../nudger-rules/nudger-rules-editor.component';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';

/** One category of switches as rendered on the page. */
interface ProcessGroup {
  category: string;
  title: string;
  blurb: string;
  processes: ServiceProcessFlag[];
}

/** One System Controls tab. The key is what ?tab= carries. */
interface SystemControlsTab {
  key: string;
  label: string;
  /** The feature switch the tab is behind; none for a tab that is always shown. */
  switchKey?: string;
}

/**
 * System controls (service processes) — runtime on/off switches for background work and outbound communications.
 *
 * Toggling saves immediately (optimistic; reverts on error) and takes effect on the API within about
 * half a minute, with no deploy or restart. Gated on ServiceProcessAdmin.
 *
 * The screen deliberately makes "something is off" hard to miss: a disabled process produces silence,
 * not an error, so the only way anyone finds out is by looking here.
 *
 * Three tabs, with the open one kept in the URL as ?tab= (switches by default): Switches holds the process
 * flags, Settings holds the runtime settings store and Nudger the tutor nudger's rules (standalone editors,
 * rendered with ngComponentOutlet so this module-declared page needs no NgModule change). The page's gate
 * covers all three. Settings and Nudger are also each behind a feature switch (settings.admin,
 * nudger.rules-editor): off, the tab is not there, and ?tab= for it opens Switches.
 */
@Component({
  selector: 'service-processes',
  templateUrl: 'service-processes.page.html',
  standalone: false
})
export class ServiceProcessesPage implements OnInit {

  public groups: ProcessGroup[] = [];
  public loading: boolean = false;
  public saving: string = null;

  private static readonly ALL_TABS: ReadonlyArray<SystemControlsTab> = [
    { key: 'switches', label: 'Switches' },
    { key: 'settings', label: 'Settings', switchKey: FeatureSwitchKeys.settingsAdmin },
    { key: 'nudger', label: 'Nudger', switchKey: FeatureSwitchKeys.nudgerRulesEditor }
  ];
  /** The tabs shown: the ungated ones, plus each switched tab once its switch is known to be on. */
  public tabs: ReadonlyArray<SystemControlsTab> = ServiceProcessesPage.ALL_TABS.filter(t => !t.switchKey);
  public selectedIndex: number = 0;
  public readonly settingsEditor = AppSettingsEditorComponent;
  public readonly nudgerEditor = NudgerRulesEditorComponent;

  /** Order and wording of the on-screen sections. Anything with an unrecognised category falls to 'Other'. */
  private static readonly GROUP_META: { category: string, title: string, blurb: string }[] = [
    {
      category: 'Master',
      title: 'Master switches',
      blurb: 'Channel-wide. Turning one of these off stops every message on that channel, whichever process asked for it.'
    },
    {
      category: 'Email',
      title: 'Email processes',
      blurb: 'Individual senders. Each is checked in addition to the email master switch, never instead of it.'
    },
    {
      category: 'Sms',
      title: 'SMS processes',
      blurb: 'Individual senders. Each is checked in addition to the SMS master switch.'
    },
    {
      category: 'Background',
      title: 'Background processes',
      blurb: 'Scheduled and triggered work. Turning one off stops it running; nothing already done is undone.'
    },
    { category: 'Other', title: 'Other', blurb: '' }
  ];

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private service: ServiceProcessService,
    private errorModalHelper: ErrorModalHelperService,
    private featureSwitches: FeatureSwitchService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // canUseGuard has already required ServiceProcessAdmin on the route.
    this.selectedIndex = Math.max(0, this.tabs.findIndex(t => t.key === this.requestedTab()));
    this.load();
    this.showSwitchedTabs();
  }

  /** Adds each switched tab whose switch is on, then reopens the tab ?tab= asked for. Fails closed. */
  private showSwitchedTabs(): void {
    const all = ServiceProcessesPage.ALL_TABS;
    combineLatest(all.map(t => t.switchKey ? this.featureSwitches.isEnabled(t.switchKey) : of(true)))
      .pipe(take(1))
      .subscribe(on => {
        this.tabs = all.filter((_, i) => on[i]);
        this.selectedIndex = Math.max(0, this.tabs.findIndex(t => t.key === this.requestedTab()));
        this.cdr.markForCheck();
      });
  }

  /** Keeps the open tab in the URL (?tab=settings), replacing the history entry rather than adding one. */
  public onTabChange(index: number): void {
    this.selectedIndex = index;
    const tab = this.tabs[index];
    if (!tab) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab.key },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  public load(): void {
    this.loading = true;
    this.service.getProcesses().subscribe({
      next: processes => {
        this.loading = false;
        this.groups = this.group(processes || []);
      },
      error: error => {
        this.loading = false;
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  /** Every process currently switched off, across all groups — drives the warning banner. */
  public get disabled(): ServiceProcessFlag[] {
    return this.groups.reduce(
      (all, g) => all.concat(g.processes.filter(p => !p.isEnabled)), [] as ServiceProcessFlag[]);
  }

  public toggle(process: ServiceProcessFlag, checked: boolean): void {
    const previous = process.isEnabled;
    process.isEnabled = checked; // optimistic
    this.saving = process.processKey;

    this.service.setProcess({ processKey: process.processKey, enabled: checked }).subscribe({
      next: () => {
        this.saving = null;
        // Re-read rather than guessing: the server stamps who changed it and when, and this is the
        // audit trail people will come looking for when they ask why something stopped sending.
        this.load();
      },
      error: error => {
        this.saving = null;
        process.isEnabled = previous; // revert on failure
        this.errorModalHelper.show('Could not change this process. Please try again.', error);
      }
    });
  }

  private requestedTab(): string {
    const snapshot = this.route && this.route.snapshot;
    const value = snapshot && snapshot.queryParamMap ? snapshot.queryParamMap.get('tab') : null;
    return (value || '').trim().toLowerCase();
  }

  private group(processes: ServiceProcessFlag[]): ProcessGroup[] {
    const known = ServiceProcessesPage.GROUP_META.map(m => m.category);

    return ServiceProcessesPage.GROUP_META
      .map(meta => ({
        category: meta.category,
        title: meta.title,
        blurb: meta.blurb,
        processes: processes
          .filter(p => meta.category === 'Other'
            ? known.indexOf(p.category) === -1
            : p.category === meta.category)
          .sort((a, b) => a.sortOrder - b.sortOrder)
      }))
      .filter(g => g.processes.length > 0);
  }
}
